import { z } from 'zod';
import { IsoTimestamp, UserId } from './common';

/**
 * Per-tenant Workday credentials. Workday accounts are tenant-scoped — every
 * `{tenant}.myworkdayjobs.com` hosts its own auth store — so a user who
 * already has, say, a Lockheed Workday account uses different creds than for
 * a Boeing Workday account. The desktop stores per-tenant overrides in the
 * macOS Keychain (#392); the worker reads them at cycle start via
 * `Keychain.getWorkdayTenantCreds()`. The schema here is kept so the desktop
 * Keychain wrapper can validate the JSON blob's shape before handing it to
 * the dispatcher — pre-#392 this schema also typed the cloud DDB row + API
 * wire shape, both of which are deleted.
 *
 * The `tenantDomain` is forced lowercase by the dispatcher's URL
 * normaliser, so callers writing to Keychain must lowercase before storing.
 */
export const WorkdayTenantCreds = z.object({
  userId: UserId,
  /**
   * Workday tenant hostname (e.g. `lockheed.wd1.myworkdayjobs.com` or
   * `boeing.wd5.myworkdayjobs.com`). Lower-cased before write — the worker
   * always looks the cred up by `new URL(applyUrl).hostname.toLowerCase()`.
   */
  tenantDomain: z.string().min(1).max(253),
  /** Email the user registered with on this tenant. */
  email: z.string().email().max(254),
  /** Password the user registered with on this tenant. */
  password: z.string().min(1).max(256),
  updatedAt: IsoTimestamp,
});
export type WorkdayTenantCreds = z.infer<typeof WorkdayTenantCreds>;
